import { z } from 'zod'
import type { MockRef, TimestampLike } from './Firebase/types'

// ============================================
// Flexible Date Type (Firestore + Supabase compat)
// ============================================

/** Flexible date field that handles both Firestore Timestamp and ISO strings */
export type FlexibleDate = Date | TimestampLike | string | null | undefined

// ============================================
// Entity Types (extend Supabase with Firestore compat)
// ============================================

export interface AtomStore {
  id: string
  name: string
  ref?: MockRef
  timezone?: string | null
  address?: string | null
  business_day_cutoff_time?: string | null
  auto_close_shift_time?: unknown
  inventory_costing_method?: string | null
  organization_id?: string | null
  is_deleted?: boolean | null
  firestore_id?: string | null
  created_at?: string | null
  updated_at?: string | null
  // Firestore camelCase aliases
  createdDate?: FlexibleDate
  businessDayCutoffTime?: string
  [key: string]: unknown
}

export interface AtomProduct {
  id: string
  name: string
  ref?: MockRef
  barcode?: string | null
  sku?: string | null
  base_sale_price?: number | null
  base_purchase_price?: number | null
  category_id?: string | null
  supplier_id?: string | null
  base_unit?: string | null
  image_url?: string | null
  description?: string | null
  has_variants?: boolean | null
  is_composite?: boolean | null
  is_variant?: boolean | null
  parent_product_id?: string | null
  ingredients?: unknown
  price_types?: unknown
  price_type_scopes?: unknown
  organization_id?: string | null
  firestore_id?: string | null
  // Runtime data from API
  stock?: Record<string, number>
  store_prices?: Record<string, number>
  storeStock?: Record<string, number>
  [key: string]: unknown
}

export interface AtomProductCategory {
  id: string
  name: string
  ref?: MockRef
  description?: string | null
  image_url?: string | null
  /** The tab order — the Firestore field, and what the backend read returns (ATL-1892). */
  order?: number | null
  display_order?: number | null
  parent_category_id?: string | null
  level?: number | null
  path?: string | null
  organization_id?: string | null
  firestore_id?: string | null
  [key: string]: unknown
}

export interface AtomSupplier {
  id: string
  name: string
  ref?: MockRef
  address?: string | null
  email?: string | null
  phone?: string | null
  contact_person?: string | null
  payment_terms?: string | null
  organization_id?: string | null
  firestore_id?: string | null
  [key: string]: unknown
}

export interface AtomRole {
  id: string
  name: string
  ref?: MockRef
  salary?: number | null
  salary_type?: string | null
  permissions?: Record<string, boolean> | null
  display_order?: number | null
  organization_id?: string | null
  firestore_id?: string | null
  // Runtime field for migration compat
  uuid?: string
  [key: string]: unknown
}

export interface AtomUser {
  id: string
  uid?: string
  email?: string
  name?: string
  active?: boolean
  organizationId?: string
  role?: string
  phone?: string
  pushToken?: string
  displayName?: string
  ref?: MockRef
  store?: { id: string; name: string }
  [key: string]: unknown
}

// ============================================
// Dictionary Types
// ============================================

export type StoresDict = Record<string, AtomStore>
export type ProductsDict = Record<string, AtomProduct>
export type ProductCategoriesDict = Record<string, AtomProductCategory>
export type SuppliersDict = Record<string, AtomSupplier>
export type RolesDict = Record<string, AtomRole>
export type UsersDict = Record<string, AtomUser>

// ============================================
// Daily Stats Types
// ============================================

export interface DailyStat {
  id?: string
  saleAmount?: number
  saleQuantity?: number
  salesProfit?: number
  salesPurchaseAmount?: number
  returnsAmount?: number
  returnsQuantity?: number
  depositsAmount?: number
  withdrawalsAmount?: number
  transfersInAmount?: number
  transfersInQuantity?: number
  transfersOutAmount?: number
  transfersOutQuantity?: number
  suppliesAmount?: number
  suppliesPaidAmount?: number
  suppliesQuantity?: number
  manufacturesAmount?: number
  manufacturesQuantity?: number
  spendingsAmount?: number
  spendingsQuantity?: number
  writeoffsAmount?: number
  writeoffsQuantity?: number
  bonusAccumulation?: number
  bonusReduction?: number
  bonusClients?: number
  shiftOpened?: boolean
  shiftOpenedTime?: FlexibleDate
  shiftClosed?: boolean
  shiftClosedTime?: FlexibleDate
  paymentMethodAmounts?: Record<string, number>
  paymentMethodQuantities?: Record<string, number>
  lastFetchDate?: Date
  hasPendingWrites?: boolean
  [key: string]: unknown
}

export type DailyStatsDict = Record<string, DailyStat>

// ============================================
// Constants Types
// ============================================

export interface Wallet {
  id: string
  name?: string
  [key: string]: unknown
}

export interface PaymentMethod {
  id: string
  name?: string
  [key: string]: unknown
}

export interface PriceType {
  id: string
  name?: string
  [key: string]: unknown
}

export interface SaleSettings {
  deliveryPrice?: number
  deliveryPaidByCustomer?: boolean
  deliveryIncludeInReceipt?: boolean
  deliveryPaymentSourceWallet?: string | null
  defaultDeliverySpendingCategory?: string | null
  showSaveCartForLater?: boolean
  allowMultipleProductSelection?: boolean
  dateFieldVisible?: boolean
  storeFieldVisible?: boolean
  tableNumberFieldVisible?: boolean
  commentFieldVisible?: boolean
  showTotalPurchasePriceInCart?: boolean
  showPurchasePriceInEditCartItem?: boolean
  mergeSimilarItems?: boolean
  [key: string]: unknown
}

export interface Constants {
  wallets?: { data: Wallet[] }
  paymentMethods?: { data: PaymentMethod[] }
  priceTypes?: { data: PriceType[] }
  saleSettings?: { data: Record<string, SaleSettings> }
  roles?: { data: AtomRole[] }
  productModifiers?: { data: ProductModifier[] }
  [key: string]: unknown
}

// ============================================
// Product Unit Types
// ============================================

export interface ProductUnit {
  id: string
  name?: string
  shortName?: string
  [key: string]: unknown
}

export type ProductUnitsDict = Record<string, ProductUnit>

// ============================================
// Derived/Computed Types
// ============================================

export interface ProductsUpdated {
  updateCount: number
  lastServerUpdate: Date | null
  lastLocalUpdate: Date | null
}

// ============================================
// AsyncStorage Persisted Types
// ============================================

export interface NewSaleInterfaceSettings {
  gridLeftWidth: number
  allowMultipleProductSelection: boolean
}

export type UserLocale = string

export type ThemePreference = 'light' | 'dark' | 'system'

// ============================================
// Zod Schemas for AsyncStorage Validation
// ============================================

export const UserLocaleSchema = z.string()

export const ThemePreferenceSchema = z.enum(['light', 'dark', 'system'])

export const NewSaleInterfaceSettingsSchema = z.object({
  gridLeftWidth: z.number().min(1).max(12),
  allowMultipleProductSelection: z.boolean(),
})

export const PrintTemplateSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  type: z.string().optional(),
  store: z.string().nullable().optional(),
  storeId: z.string().optional(),
  content: z.any().optional(),
  settings: z.any().optional(),
  isDefault: z.boolean().optional(),
  ref: z.any().optional(),
}).passthrough()

export const PrintTemplatesSchema = z.array(PrintTemplateSchema)

// ============================================
// Discount Options
// ============================================

export interface DiscountOption {
  id: string
  name?: string
  value?: number
  type?: 'percentage' | 'fixed'
  [key: string]: unknown
}

// ============================================
// Product Modifiers
// ============================================

export interface ProductModifier {
  id: string
  name?: string
  options?: { id: string; name: string; price?: number }[]
  [key: string]: unknown
}
